import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Export data to Excel file
 */
export const exportToExcel = (data, filename, sheetName = 'Sheet1') => {
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    
    // Auto-size columns
    const maxWidth = 50;
    const colWidths = {};
    
    // Get headers
    if (data.length > 0) {
        Object.keys(data[0]).forEach((key, i) => {
            const headerLength = key.length;
            const maxDataLength = Math.max(
                ...data.map(row => String(row[key] || '').length)
            );
            colWidths[i] = Math.min(Math.max(headerLength, maxDataLength) + 2, maxWidth);
        });
        
        worksheet['!cols'] = Object.values(colWidths).map(w => ({ wch: w }));
    }
    
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    // Generate and download
    XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Export data to PDF file
 */
export const exportToPDF = (data, filename, title, columns) => {
    const doc = new jsPDF('landscape', 'pt', 'a4');
    
    // Add title
    doc.setFontSize(18);
    doc.setTextColor(13, 148, 136); // Primary color (teal)
    doc.text(title, 40, 40);
    
    // Add date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })}`, 40, 60);
    
    // Add HAAPE branding
    doc.setFontSize(10);
    doc.text('HAAPE Hub for Employment & Support', doc.internal.pageSize.width - 40, 40, { align: 'right' });
    
    // Prepare table data
    const tableColumns = columns.map(col => ({ 
        header: col.header, 
        dataKey: col.key 
    }));
    
    const tableData = data.map(row => {
        const rowData = {};
        columns.forEach(col => {
            let value = row[col.key];
            // Handle arrays
            if (Array.isArray(value)) {
                value = value.join(', ');
            }
            // Handle booleans
            if (typeof value === 'boolean') {
                value = value ? 'Yes' : 'No';
            }
            // Handle null/undefined
            if (value === null || value === undefined) {
                value = '';
            }
            // Truncate long text
            if (typeof value === 'string' && value.length > 100) {
                value = value.substring(0, 100) + '...';
            }
            rowData[col.key] = value;
        });
        return rowData;
    });
    
    // Generate table using autoTable
    autoTable(doc, {
        columns: tableColumns,
        body: tableData,
        startY: 80,
        styles: {
            fontSize: 8,
            cellPadding: 4,
        },
        headStyles: {
            fillColor: [13, 148, 136],
            textColor: 255,
            fontStyle: 'bold',
        },
        alternateRowStyles: {
            fillColor: [245, 245, 245],
        },
        margin: { top: 80, left: 40, right: 40 },
        didDrawPage: (data) => {
            // Add page number
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text(
                `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 20,
                { align: 'center' }
            );
        }
    });
    
    // Save
    doc.save(`${filename}.pdf`);
};

/**
 * Format resource data for export
 */
export const formatResourcesForExport = (resources) => {
    return resources.map(r => ({
        'Organization Name': r.name || '',
        'Type': r.stakeholder_type || r.category || '',
        'State': r.state || '',
        'City': r.city || '',
        'Mission': r.mission || '',
        'Website': r.website || '',
        'Contact Name': r.contact_name || '',
        'Contact Email': r.contact_email || '',
        'Contact Phone': r.contact_phone || '',
        'MOU Status': r.mou_status || '',
        'Adults Employed (Annual)': r.adults_employed_annually || '',
        'Adults Employed (All-Time)': r.adults_employed_all_time || '',
        'Adults Impacted (Annual)': r.adults_impacted_annually || '',
        'Adults Impacted (All-Time)': r.adults_impacted_all_time || '',
        'Completion Rate': r.program_completion_rate ? `${r.program_completion_rate}%` : '',
        'Placement Rate': r.job_placement_rate ? `${r.job_placement_rate}%` : '',
        'Approved': r.approved ? 'Yes' : 'No',
        'Featured': r.featured ? 'Yes' : 'No',
        'Created': r.created_at ? new Date(r.created_at).toLocaleDateString() : '',
    }));
};

/**
 * Format member data for export
 */
export const formatMembersForExport = (members) => {
    return members.map(m => ({
        'Name': m.name || '',
        'Email': m.email || '',
        'Title': m.title || '',
        'Organization': m.organization || '',
        'City': m.city || '',
        'State': m.state || '',
        'Bio': m.bio || '',
        'Is Admin': m.is_admin ? 'Yes' : 'No',
        'Joined': m.created_at ? new Date(m.created_at).toLocaleDateString() : '',
    }));
};

/**
 * Resource columns for PDF export
 */
export const resourcePDFColumns = [
    { header: 'Organization', key: 'Organization Name' },
    { header: 'Type', key: 'Type' },
    { header: 'State', key: 'State' },
    { header: 'City', key: 'City' },
    { header: 'Contact', key: 'Contact Name' },
    { header: 'Email', key: 'Contact Email' },
    { header: 'MOU Status', key: 'MOU Status' },
    { header: 'Employed (Annual)', key: 'Adults Employed (Annual)' },
    { header: 'Impacted (Annual)', key: 'Adults Impacted (Annual)' },
];

/**
 * Member columns for PDF export
 */
export const memberPDFColumns = [
    { header: 'Name', key: 'Name' },
    { header: 'Email', key: 'Email' },
    { header: 'Title', key: 'Title' },
    { header: 'Organization', key: 'Organization' },
    { header: 'City', key: 'City' },
    { header: 'State', key: 'State' },
    { header: 'Admin', key: 'Is Admin' },
    { header: 'Joined', key: 'Joined' },
];
